import { useMutation, useQueryClient } from 'react-query';
import { ITcoLocations } from 'types';
import { tcoLocationRequest, queryKeys } from 'utils';

export const useUpdateTcoLocations = () => {
  const queryClient = useQueryClient();

  const {
    mutate: updateTcoLocations,
    isLoading: updateTcoLocationsLoading,
    isSuccess: updateTcoLocationsSuccess,
    isError: updateTcoLocationsError,
  } = useMutation(
    async (updated: Partial<ITcoLocations>) => {
      const processedLocation = {
        ...updated,
        underlaySelections: updated.underlaySelections?.map(
          (selection: any) => {
            if (!selection.id || selection.id.startsWith('new-')) {
              const { id, createdAt, updatedAt, ...selectionForInsert } =
                selection;
              selectionForInsert.locationId = updated.id;
              return selectionForInsert;
            }
            const { createdAt, updatedAt, ...existingSelectionForUpdate } =
              selection;
            return existingSelectionForUpdate;
          }
        ),
      };

      const resp = await tcoLocationRequest({
        endpoint: `tco-locations`,
        method: 'PUT',
        data: processedLocation,
      });
      return resp as ITcoLocations;
    },
    {
      onMutate: async (updated: Partial<ITcoLocations>) => {
        if (updated.estimateId) {
          const locationsKey = queryKeys({ estimateId: updated.estimateId })
            .filter.tcoLocations;
          await queryClient.cancelQueries(locationsKey);

          const prevLocations =
            queryClient.getQueryData<ITcoLocations[]>(locationsKey) || [];

          queryClient.setQueryData<ITcoLocations[]>(
            locationsKey,
            prevLocations.map((loc) =>
              loc.id === updated.id
                ? ({ ...loc, ...updated } as ITcoLocations)
                : loc
            )
          );

          return { prevLocations };
        }
        return {};
      },
      onError: (err, updated: Partial<ITcoLocations>, context: any) => {
        console.log(err);
        if (updated.estimateId && context?.prevLocations) {
          const locationsKey = queryKeys({ estimateId: updated.estimateId })
            .filter.tcoLocations;
          queryClient.setQueryData(locationsKey, context.prevLocations);
        }
      },
      onSettled: (_, __, updated: Partial<ITcoLocations>) => {
        if (updated.estimateId) {
          const locationsKey = queryKeys({ estimateId: updated.estimateId })
            .filter.tcoLocations;
          queryClient.invalidateQueries(locationsKey);
        }
      },
    }
  );

  return {
    updateTcoLocations,
    updateTcoLocationsLoading,
    updateTcoLocationsSuccess,
    updateTcoLocationsError,
  };
};
